import type { TreeOption } from 'naive-ui';
import { FeatureTreePresentationIndex } from './FeatureTreePresentationIndex';
import { FeatureTreeRuntimeSubtreeProjector } from './FeatureTreeRuntimeSubtreeProjector';

/**
 * 保存一次完整树刷新期间的有序结构差量，并重放到晚返回的候选树。
 *
 * 记录只在刷新请求存活期间持有纯 UI 数据。同父节点的不同操作不能互相覆盖，
 * 嵌套子树操作也必须保持提交顺序；快照与活动树分离，避免后续修改污染先前操作。
 */
export class FeatureTreeRuntimeSubtreeReplay<TNodeMeta extends object> {
  /** 当前允许记录结构差量的完整刷新请求。 */
  private requestId: number | null = null;
  /** 按真实提交顺序保存的结构快照。 */
  private readonly updates: GlbRuntimeSubtreeUpdate<TNodeMeta>[] = [];

  /** @param requestId 新完整刷新请求；新请求重新读取权威 hierarchy，旧记录即失效。 */
  public begin(requestId: number): void {
    this.clear();
    this.requestId = requestId;
  }

  /** @param requestId 已结束的请求；迟到旧请求不得清除新请求的记录。 */
  public end(requestId: number): void {
    if (this.requestId === requestId) this.clear();
  }

  /** 取消当前请求记录并释放全部树节点和元数据快照。 */
  public clear(): void {
    this.requestId = null;
    this.updates.length = 0;
  }

  /** @param update 已成功应用到活动树的差量；无在途刷新时无需保留重放副本。 */
  public record(update: GlbRuntimeSubtreeUpdate<TNodeMeta>): void {
    if (this.requestId !== null) this.updates.push(this.copyUpdate(update));
  }

  /**
   * 把本请求全部结构操作按顺序应用到候选树。
   *
   * @param tree 晚返回的完整候选树。
   * @param nodeMap 与候选树配套的元数据。
   * @param index 候选树专用节点索引。
   * @param projector 与活动树共用的结构提交规则。
   */
  public replay(
    tree: readonly TreeOption[],
    nodeMap: Map<string, TNodeMeta>,
    index: FeatureTreePresentationIndex,
    projector: FeatureTreeRuntimeSubtreeProjector,
  ): void {
    for (const update of this.updates) {
      // 后续操作可能修改本次插入的后代；每次重放都从独立副本开始，保留记录本身。
      if (!projector.apply(tree, nodeMap, index, this.copyUpdate(update))) {
        throw new Error(`设计树结构重放缺少父节点：${update.parentKey}`);
      }
    }
  }

  /**
   * 复制结构差量的可变树结构与元数据记录，保留图标/标签等只读展示值。
   *
   * @param update 要冻结的结构操作。
   * @returns 不与活动树共享节点或 children 数组的操作快照。
   */
  private copyUpdate(update: GlbRuntimeSubtreeUpdate<TNodeMeta>): GlbRuntimeSubtreeUpdate<TNodeMeta> {
    const children = update.children.map(child => this.copyNode(child));
    const childByKey = new Map(children.map(child => [child.key, child]));
    return {
      parentKey: update.parentKey,
      removedRootKeys: [...update.removedRootKeys],
      removedNodeKeys: [...update.removedNodeKeys],
      children,
      ...(update.childInsertions ? {
        childInsertions: update.childInsertions.map(insertion => ({
          index: insertion.index,
          child: childByKey.get(insertion.child.key) ?? this.copyNode(insertion.child),
        })),
      } : {}),
      nodeMapEntries: new Map([...update.nodeMapEntries].map(([key, meta]) => [key, { ...meta }])),
    };
  }

  /** @param node 纯 UI 节点。 @returns 复制后的节点及递归 children，不克隆函数或图标资源。 */
  private copyNode(node: TreeOption): TreeOption {
    return {
      ...node,
      ...(Array.isArray(node.children) ? { children: node.children.map(child => this.copyNode(child)) } : {}),
    };
  }
}
